import React, { useEffect, useState, useCallback } from 'react';
import { Search, Download, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { api, API_URL } from './api';

interface AzUser {
  id: number;
  name: string | null;
  email: string;
  interest: string | null;
  source: string;
  created_at: string;
}

interface UsersResponse {
  users: AzUser[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AzUser[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback((page: number, searchTerm: string) => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: '20' });
    if (searchTerm) params.set('search', searchTerm);

    api.get<UsersResponse>(`/api/users?${params.toString()}`)
      .then(({ users, pagination }) => { setUsers(users); setPagination(pagination); })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load users.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(1, search); }, [load, search]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this user record? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await api.delete(`/api/users/${id}`);
      load(pagination.page, search);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete user.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('azura_admin_token');
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const res = await fetch(`${API_URL}/api/users/export?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error('Export failed.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `azura-users-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-figtree font-black text-2xl text-white">Users</h1>
          <p className="text-neutral-500 text-sm font-exo">{pagination.total} total signups</p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <form onSubmit={handleSearchSubmit} className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder="Search name or email…"
              className="bg-neutral-900 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-accent-amber transition-all w-full sm:w-64" />
          </form>
          <button onClick={handleExport} disabled={exporting}
            className="inline-flex items-center justify-center gap-2 bg-neutral-900 border border-white/10 text-white px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider hover:border-accent-amber transition-all disabled:opacity-60 shrink-0">
            <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      <div className="bg-neutral-900 border border-white/10 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="px-4 sm:px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-neutral-500">Name</th>
                <th className="px-4 sm:px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-neutral-500">Email</th>
                <th className="hidden md:table-cell px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-neutral-500">Interest</th>
                <th className="hidden md:table-cell px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-neutral-500">Source</th>
                <th className="hidden sm:table-cell px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-neutral-500">Signed Up</th>
                <th className="px-4 sm:px-5 py-3.5" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-neutral-500">Loading…</td></tr>
              ) : error ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-red-400">{error}</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-neutral-500">No users found.</td></tr>
              ) : (
                users.map(u => (
                  <tr key={u.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                    <td className="px-4 sm:px-5 py-3.5 text-white">{u.name || '—'}</td>
                    <td className="px-4 sm:px-5 py-3.5 text-neutral-300 break-all">{u.email}</td>
                    <td className="hidden md:table-cell px-5 py-3.5 text-neutral-400">{u.interest || '—'}</td>
                    <td className="hidden md:table-cell px-5 py-3.5 text-neutral-400 capitalize">{u.source}</td>
                    <td className="hidden sm:table-cell px-5 py-3.5 text-neutral-500 text-xs">{new Date(u.created_at).toLocaleString()}</td>
                    <td className="px-4 sm:px-5 py-3.5 text-right">
                      <button onClick={() => handleDelete(u.id)} disabled={deletingId === u.id}
                        className="text-neutral-500 hover:text-red-400 transition-colors disabled:opacity-50" aria-label="Delete user">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-white/10">
          <p className="text-xs text-neutral-500">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => load(pagination.page - 1, search)} disabled={pagination.page <= 1}
              className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-neutral-400 hover:text-white hover:border-white/30 disabled:opacity-40 transition-all">
              <ChevronLeft size={15} />
            </button>
            <button onClick={() => load(pagination.page + 1, search)} disabled={pagination.page >= pagination.totalPages}
              className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-neutral-400 hover:text-white hover:border-white/30 disabled:opacity-40 transition-all">
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
